import { useCallback } from 'react';
import { useQuery } from 'react-query';
import {
  AllSystemDomains,
  AllSystemState,
  getAllSystemState
} from 'src/Api/exchanges';
import { SYSTEM_ALL_STATE_KEY } from './useSystemKillSwitch';

export type PredictionDomain = keyof AllSystemDomains;

/**
 * Per-domain presence flags, every one of them nullable: `null` means the
 * backend did not report it. Nothing here is defaulted — an un-reported domain
 * stays unknown so callers can fail OPEN instead of gating on a guess.
 */
export interface DomainPresence {
  booted: boolean | null;
  nodeReady: boolean | null;
  isRunning: boolean | null;
  armed: boolean | null;
  wanted: boolean | null;
  down: boolean | null;
}

const UNKNOWN_PRESENCE: DomainPresence = {
  booted: null,
  nodeReady: null,
  isRunning: null,
  armed: null,
  wanted: null,
  down: null
};

const asBool = (value: unknown): boolean | null =>
  typeof value === 'boolean' ? value : null;

type RawDomainPresence = Record<string, unknown>;

/**
 * Typed read of the OPTIONAL `domains` block on `/api/system/all/state`. Uses
 * the kill switch's key + fetcher, so both observers share ONE cache entry and
 * one in-flight request; react-query 3 arms `refetchInterval` per observer, so
 * this does add its own 30s timer — kept deliberately, so the presence data
 * still refreshes on screens where the kill-switch button is not mounted.
 *
 * `domains` only exists on P5+ backends. When it is absent — or while the query
 * is loading, or after it errors — every field reads `null`, never `false`.
 */
export function useDomainPresence() {
  const query = useQuery<AllSystemState>(
    [SYSTEM_ALL_STATE_KEY],
    getAllSystemState,
    {
      refetchInterval: 30000,
      retry: 1
    }
  );

  const domains = query.data?.domains ?? null;

  /** The typed per-domain view. Unknown fields stay `null`. */
  const get = useCallback(
    (domain: PredictionDomain): DomainPresence => {
      const raw = (domains as Record<string, RawDomainPresence> | null)?.[
        domain
      ];
      if (!raw || typeof raw !== 'object') return UNKNOWN_PRESENCE;
      return {
        booted: asBool(raw.booted),
        nodeReady: asBool(raw.nodeReady),
        isRunning: asBool(raw.isRunning),
        armed: asBool(raw.armed),
        wanted: asBool(raw.wanted),
        down: asBool(raw.down)
      };
    },
    [domains]
  );

  /**
   * True ONLY when the backend positively reports the domain's rule-CRUD pod
   * as down. Unknown (`null`) is NOT down — the gate fails open.
   */
  const isNodeDown = useCallback(
    (domain: PredictionDomain): boolean => get(domain).nodeReady === false,
    [get]
  );

  return {
    get,
    isNodeDown,
    /**
     * Timestamp of the last SUCCESSFUL read (0 before the first one). Consumers
     * that debounce a state change count distinct polls with it — a re-render
     * alone must not be mistaken for fresh evidence.
     */
    dataUpdatedAt: query.dataUpdatedAt
  };
}
